import {
  campaignApiDelete,
  campaignApiGet,
  campaignApiPatch,
  campaignApiPost,
} from '@/lib/api/campaign-api-client';
import type {
  AttributeDefinitionApi,
  AttributeEntityResponseApi,
  AttributePagedResponseApi,
  AttributePagedResponseEntityResponseApi,
  CreateAttributeRequestApi,
  GetAttributesRestParams,
  UpdateAttributeRequestApi,
} from '../types/attributes-api.types';
import type {
  CustomFieldDefinition,
  CustomFieldDefinitionsPagingResult,
  InsertCustomFieldDefinitionResult,
  UpdateCustomFieldDefinitionResult,
} from '../types/custom-field-definitions.types';
import { mapAttributeApiToDefinition } from '../utils/attribute-definition-mapper';

const assertCampaignApiSuccess = <T>(
  response: { success?: boolean; message?: string | null; errors?: string[] | null; data?: T } | null | undefined,
  errorLabel: string
): T => {
  if (!response || typeof response !== 'object') {
    throw new Error(`${errorLabel}: invalid response structure`);
  }

  if (response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      errorLabel;
    throw new Error(message);
  }

  const data = response.data;
  if (data === undefined || data === null) {
    throw new Error(`${errorLabel}: missing data payload`);
  }

  return data;
};

const toPagingResult = (
  items: CustomFieldDefinition[],
  totalCount: number,
  pageNo: number,
  pageSize: number
): CustomFieldDefinitionsPagingResult => {
  const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;

  return {
    items,
    totalCount,
    pageNo,
    pageSize,
    totalPages,
    hasNextPage: pageNo < totalPages,
    hasPreviousPage: pageNo > 1,
  };
};

const mapItems = (rawItems: AttributeDefinitionApi[] | null | undefined): CustomFieldDefinition[] =>
  (rawItems ?? [])
    .map((item) => mapAttributeApiToDefinition(item))
    .filter((item): item is CustomFieldDefinition => item !== null);

export const getAttributesFromRest = async (
  params: GetAttributesRestParams = {}
): Promise<CustomFieldDefinitionsPagingResult> => {
  const pageNo = params.pageNo ?? 1;
  const pageSize = params.pageSize ?? 100;
  const offset = Math.max(0, (pageNo - 1) * pageSize);

  const response = await campaignApiGet<AttributePagedResponseEntityResponseApi>('/v1/attributes', {
    Limit: pageSize,
    Offset: offset,
  });

  const data = assertCampaignApiSuccess<AttributePagedResponseApi | AttributeDefinitionApi[]>(
    response,
    'Failed to fetch custom field definitions'
  );

  if (Array.isArray(data)) {
    const items = mapItems(data);
    return toPagingResult(items, items.length, pageNo, pageSize);
  }

  const items = mapItems(data.items);
  const totalCount = Number(data.pageInfo?.total ?? items.length);

  return toPagingResult(items, totalCount, pageNo, pageSize);
};

export const createAttributeFromRest = async (
  input: CreateAttributeRequestApi
): Promise<InsertCustomFieldDefinitionResult> => {
  const response = await campaignApiPost<AttributeEntityResponseApi>('/v1/attributes', input);

  if (response && typeof response === 'object' && response.success === false) {
    const fromErrors = Array.isArray(response.errors)
      ? response.errors.filter((entry): entry is string => typeof entry === 'string' && Boolean(entry.trim()))
      : [];
    const message =
      (fromErrors.length > 0 ? fromErrors.join(', ') : undefined) ||
      response.message ||
      'Failed to create custom field';
    throw new Error(message);
  }

  const data = response?.data;
  const definition = data ? mapAttributeApiToDefinition(data) : null;
  const itemId = definition?.ItemId?.trim() || input.key;

  if (!itemId) {
    throw new Error(response?.message || 'Failed to create custom field');
  }

  return {
    acknowledged: true,
    itemId,
    totalImpactedData: 1,
    message: response?.message ?? undefined,
  };
};

export const updateAttributeFromRest = async (params: {
  key: string;
  input: UpdateAttributeRequestApi;
}): Promise<UpdateCustomFieldDefinitionResult> => {
  const key = params.key.trim();
  if (!key) {
    throw new Error('Attribute key is required');
  }

  const response = await campaignApiPatch<AttributeEntityResponseApi>(
    `/v1/attributes/${encodeURIComponent(key)}`,
    params.input
  );
  const data = assertCampaignApiSuccess(response, 'Failed to update custom field');
  const definition = mapAttributeApiToDefinition(data);
  const itemId = definition?.ItemId?.trim() || key;

  return {
    acknowledged: true,
    itemId,
    totalImpactedData: 1,
    message: response.message ?? undefined,
  };
};

export const deleteAttributeFromRest = async (key: string): Promise<UpdateCustomFieldDefinitionResult> => {
  const attributeKey = key.trim();
  if (!attributeKey) {
    throw new Error('Attribute key is required');
  }

  const response = await campaignApiDelete<AttributeEntityResponseApi>(
    `/v1/attributes/${encodeURIComponent(attributeKey)}`
  );

  if (response && typeof response === 'object' && response.success === false) {
    const message =
      response.message ||
      (Array.isArray(response.errors) ? response.errors.join(', ') : undefined) ||
      'Failed to delete custom field';
    throw new Error(message);
  }

  return {
    acknowledged: true,
    itemId: attributeKey,
    totalImpactedData: 1,
    message: response?.message ?? undefined,
  };
};
